import { formatHeading, formatTable } from './utils';

interface SuggestedKeyword {
  keyword: string;
  volume: number;
  cpc: string;
  currency?: string;
  competition: number;
}

/**
 * Format SEO Keyword Research data for copying as Markdown
 */
export const formatSEOKeywordCopy = (data: any): string => {
  if (!data || (!data.suggested_keywords && !data.seo_title)) {
    return 'No SEO keyword data available';
  }

  const sections: string[] = [];

  // Header
  sections.push(formatHeading('SEO Keyword Research', 1));
  sections.push('');

  // SEO Title
  if (data.seo_title) {
    sections.push(`**SEO Title:** ${data.seo_title}`);
    sections.push('');
  }

  // Meta Description
  if (data.meta_description) {
    sections.push(`**Meta Description:** ${data.meta_description}`);
    sections.push('');
  }

  // Suggested Keywords Table
  if (data.suggested_keywords && data.suggested_keywords.length > 0) {
    sections.push(formatHeading(`Suggested Keywords (${data.suggested_keywords.length})`, 2));
    sections.push('');

    // Build markdown table
    const headers = ['Keyword', 'Volume', 'CPC', 'Competition'];
    const rows = data.suggested_keywords.map((keyword: SuggestedKeyword) => {
      return [
        keyword.keyword,
        keyword.volume.toLocaleString(),
        `${keyword.currency || '$'}${keyword.cpc}`,
        `${(keyword.competition * 100).toFixed(0)}%`,
      ];
    });

    sections.push(formatTable(headers, rows));
    sections.push('');
  }

  // Top Competitors
  if (data.top_competitors && data.top_competitors.length > 0) {
    sections.push(formatHeading(`Top Competitors (${data.top_competitors.length})`, 2));
    sections.push('');
    data.top_competitors.forEach((competitor: string, index: number) => {
      sections.push(`${index + 1}. ${competitor}`);
    });
    sections.push('');
  }

  // Response Text (if available) - raw analysis without tables
  if (data.response_text) {
    sections.push(formatHeading('Analysis', 2));
    sections.push('');

    // Keep the analysis text but remove tables since we already have structured tables above
    const textWithoutTables = removeMarkdownTables(data.response_text);

    if (textWithoutTables.trim()) {
      sections.push(textWithoutTables);
    }
  }

  return sections.join('\n');
};

/**
 * Remove markdown tables from text
 */
function removeMarkdownTables(text: string): string {
  const lines = text.split('\n');
  const filteredLines: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const nextLine = i + 1 < lines.length ? lines[i + 1] : '';

    // Check if this line starts a markdown table
    const isTableHeader = line.includes('|') && nextLine.includes('|') && nextLine.includes('-');

    if (isTableHeader) {
      // Skip the table header
      i++;
      // Skip the separator
      i++;
      // Skip all rows
      while (i < lines.length && lines[i].includes('|')) {
        i++;
      }
      // Skip empty line after table if exists
      if (i < lines.length && lines[i].trim() === '') {
        i++;
      }
    } else {
      // Keep non-table lines, but skip lines that introduce tables
      const lowerLine = line.toLowerCase().trim();
      if (
        !lowerLine.includes('below is a summary') &&
        !lowerLine.includes('here is a summary') &&
        !lowerLine.includes('table of keywords') &&
        !lowerLine.includes('see the table')
      ) {
        filteredLines.push(line);
      }
      i++;
    }
  }

  return filteredLines.join('\n').trim();
}
